import React, { ChangeEvent, useEffect, useState } from 'react'
import { DropdownToggle, DropdownMenu, Dropdown } from 'reactstrap'
import styled from 'styled-components'
import Button from './Button'
import InlineButton from './InlineButton'

import { ReactComponent as DropdownIconClosed } from '../../images/dropdownIconClosed.svg'
import SearchIcon from '../../images/SearchBar/search.svg'
import Checkbox from './Checkbox'

const StyledButton = styled(Dropdown)<{ isOpen: boolean }>`
  svg.arrow-toggle {
    pointer-events: none;
    position: absolute;
    top: 13px;
    right: 20px;

    transform: ${({ isOpen }) =>
      isOpen ? 'rotate(180deg)' : 'rotate(0)'};

    transition: transform 300ms ease;

    path {
      fill: ${({ isOpen }) => isOpen && 'white'};
    }
  }

  .toggle {
    color: ${({ theme }) => theme.colors.white};
    margin-right: 10px;
    padding-left: 10px;
    height: 36px;
    width: 138px;
    background-color: ${({ theme }) => theme.colors.gray2};
    border-radius: ${({ isOpen }) => (isOpen ? '3px 3px 0 0' : '3px')};
    border: ${({ isOpen }) =>
      isOpen ? '1px solid #9093a1;' : '1px solid transparent'};
    :focus {
      box-shadow: none !important;
    }
  }
`

const StyledMenu = styled(DropdownMenu)`
  font-size: ${({ theme }) => theme.fontSize.s12};

  color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25);
  margin: 0;
  overflow: hidden;

  width: 150px;
  padding: 10px 10px;
  background-color: ${({ theme }) => theme.colors.gray1};
  border: 1px solid #9093a1;
  border-radius: 0 0 3px 3px;
`
const SearchInput = styled.input<{ icon: string }>`
  background: transparent;
  background: url(${({ icon }) => icon}) no-repeat scroll;
  background-position: -5px;
  padding-left: 20px;
  color: #9093a1;
  width: 100%;
  bottom: -6px;
  padding-block: 5px;
  border: 1px solid transparent;

  ::placeholder {
    color: ${({ theme }) => theme.colors.gray4};
  }
  ::-webkit-input-placeholder {
    /* Chrome/Opera/Safari */
    color: ${({ theme }) => theme.colors.gray4};
  }
  ::-moz-placeholder {
    /* Firefox 19+ */
    color: ${({ theme }) => theme.colors.gray4};
  }
  :-ms-input-placeholder {
    /* IE 10+ */
    color: ${({ theme }) => theme.colors.gray4};
  }
  :-moz-placeholder {
    /* Firefox 18- */
    color: ${({ theme }) => theme.colors.gray4};
  }

  :focus {
    border-bottom: ${({ theme }) => theme.colors.white} solid 1px;
    background-color: ${({ theme }) => `${theme.colors.white}26`};
    border-radius: 3px 3px 0 0;
  }
`

const Underline = styled.div`
  left: 0;
  width: 100%;
  height: 1px;
  background-color: white;
  opacity: 0.1;
  position: absolute;
`

const Selected = styled.div`
  color: ${({ theme }) => theme.colors.gray4};
  display: flex;
  margin-block: 10px;
`
const ClearAllButton = styled.button`
  color: ${({ theme }) => theme.colors.white};
  position: absolute;
  padding: 0;
  text-decoration: underline;
  right: 20px;
`
const CheckBoxSection = styled.div`
  flex-direction: column;
  margin-block: 20px 10px;
  overflow: auto;
  max-height: 200px;
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
  float: right;

  .inline-btn {
    margin-right: 10px;
  }
`

type Props = {
  id: string
  onFilterApply: (filter: string[]) => void
  className?: string
  name?: string
  filterType?: string
  checkboxOptions: string[]
}

const FilterDropdown: React.FC<Props> = (props) => {
  const { name, checkboxOptions, onFilterApply } = props
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [countState, setCountState] = useState(0)

  const initialValue = checkboxOptions.reduce(
    (options, option) => ({
      ...options,
      [option]: false,
    }),
    {}
  )

  const [checkboxes, setCheckboxes] = useState<{ [key: string]: boolean }>(
    initialValue
  )
  const [filterSearch, setFilterSearch] = useState('')

  useEffect(() => {
    let count = 0
    Object.keys(checkboxes).forEach((checkbox) => {
      checkboxes[checkbox] && count++
    })
    setCountState(count)
  }, [checkboxes])

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target

    setCheckboxes({
      ...checkboxes,
      [name]: !checkboxes[name],
    })
  }

  const handleFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    setFilterSearch(e.target.value)
  }

  const toggle = () => setDropdownOpen(!dropdownOpen)
  const clearAll = () => setCheckboxes(initialValue)

  const handleApply = () => {
    //Array of selected checkboxes
    const selectedItems = Object.keys(checkboxes).filter(
      (checkbox) => checkboxes[checkbox]
    )
    onFilterApply(selectedItems)
  }

  const createCheckbox = checkboxOptions
    .filter(
      (options) =>
        options.toLowerCase().includes(filterSearch) || filterSearch === ''
    )
    .map((option) => (
      <Checkbox
        label={option}
        isChecked={checkboxes[option]}
        onChange={handleCheckboxChange}
        key={option}
      />
    ))

  return (
    <StyledButton {...props} isOpen={dropdownOpen} toggle={toggle}>
      <DropdownIconClosed className={'arrow-toggle'} />
      <DropdownToggle className="toggle">{name}</DropdownToggle>
      <StyledMenu>
        <SearchInput
          icon={SearchIcon}
          placeholder={'Search'}
          value={filterSearch}
          onChange={handleFilterChange}
        />
        <Underline />
        <Selected>
          {countState} Selected
          <ClearAllButton onClick={clearAll}>Clear All</ClearAllButton>
        </Selected>
        <Underline />
        <CheckBoxSection>{createCheckbox}</CheckBoxSection>
        <Underline />
        <Footer>
          <InlineButton onClick={toggle} className="inline-btn" white>
            Cancel
          </InlineButton>
          <Button
            onClick={() => {
              toggle()
              handleApply()
            }}
          >
            Apply
          </Button>
        </Footer>
      </StyledMenu>
    </StyledButton>
  )
}

export default FilterDropdown
